import React, { useState, useEffect, useCallback } from 'react'
import {
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Table,
  message,
  Dropdown,
  Tooltip,
  Card,
  Space,
  Switch
} from 'antd'
import styled from 'styled-components'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {
  EllipsisOutlined,
  InfoCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  FieldTimeOutlined,
  PlusCircleOutlined,
  UpOutlined,
  DownOutlined,
  CopyOutlined
} from '@ant-design/icons'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import { DatePicker as MobileDatePicker } from 'antd-mobile'
// ⚠️ 兩種相反的時間，別搞混：
//   使用者在 RangePicker 挑的值 → 牆上時間 → fromPicker()
//   後端回來的 start / end（ISO 絕對時刻）→ inTz()
import { fromPicker, inTz, now as tzNow } from '../utils/timezone'

dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(timezone)

const { RangePicker } = DatePicker

const Container = styled.div`
  max-width: 800px;
  margin: auto;
  padding: 10px 0;
`

const Hint = styled.div`
  font-size: 14px;
  color: #666;
  background-color: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 12px;
  margin: 16px 0;
`

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 8px;
  margin-top: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    max-height: 400px;
    overflow-y: auto;
    padding-right: 8px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(3, 1fr);
    max-height: 320px;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`

const TimeSlot = styled.button`
  padding: 8px;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  background: ${(props) => (props.selected ? '#bf1c5f' : '#f5f5f5')};
  color: ${(props) => (props.selected ? '#fff' : '#666')};
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
  font-size: 14px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin: 16px 0;
  align-items: center;

  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;

    .color-box {
      width: 16px;
      height: 16px;
      border-radius: 4px;
    }
  }
`

const QuickActionButton = styled(Button)`
  width: 100%;
  height: 48px;
  border-radius: 8px;
  border: none;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .anticon {
    font-size: 20px;
  }

  &.full-day {
    background: linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%);
    color: #fff;
    padding: 0 5px;
    &:hover {
      color: #fff !important;
      background: linear-gradient(135deg, #ff8a8e 0%, #f9c0b4 100%) !important;
    }
  }

  &.range {
    background: linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%);
    color: #fff;
    padding: 0 5px;
    &:hover {
      color: #fff !important;
      background: linear-gradient(135deg, #917cd1 0%, #fab2eb 100%) !important;
    }
  }

  &.add-new {
    background: linear-gradient(135deg, #f899ac 0%, #f9c1cc 100%);
    color: #fff;
    padding: 0 5px;
    &:hover {
      color: #fff !important;
      background: linear-gradient(135deg, #f4758e 0%, #f4acba 100%) !important;
    }
  }
  @media (max-width: 576px) {
    height: 44px;
    font-size: 15px;
    .anticon {
      font-size: 16px;
    }
  }
`

const QuickActionCard = styled.div`
  .action-buttons {
    display: flex;
    gap: 12px;
    padding: 8px 0;
  }
  @media (max-width: 576px) {
    flex-direction: column;
  }

  .ant-card-body {
    padding: 16px;
  }
`

const CompanyDaysOff = () => {
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [daysOff, setDaysOff] = useState([])
  const [form] = Form.useForm()
  const [editForm] = Form.useForm()
  const [selectedYear, setSelectedYear] = useState(tzNow().year())
  const [editRecord, setEditRecord] = useState(null)
  const { user } = useAuth()
  const businessId = user.businessId
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTimeSlots, setSelectedTimeSlots] = useState({})
  const [showMidnight, setShowMidnight] = useState(false)
  const [quickActionModalVisible, setQuickActionModalVisible] = useState(false)
  const [quickActionType, setQuickActionType] = useState('fullDay')
  const [quickActionDateRange, setQuickActionDateRange] = useState(null)
  const [isCopyModalVisible, setIsCopyModalVisible] = useState(false)
  const [copyDateRange, setCopyDateRange] = useState(null)
  const [repeatEnabled, setRepeatEnabled] = useState(false)
  const [selectedDays, setSelectedDays] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [isCollapsed, setIsCollapsed] = useState(true)
  const pageSize = 10
  const [copySourceSlots, setCopySourceSlots] = useState({})
  const [showMobileStartPicker, setShowMobileStartPicker] = useState(false)
  const [showMobileEndPicker, setShowMobileEndPicker] = useState(false)
  const [mobileStartDate, setMobileStartDate] = useState(null)
  const [mobileEndDate, setMobileEndDate] = useState(null)
  const [showQuickMobileStartPicker, setShowQuickMobileStartPicker] =
    useState(false)
  const [showQuickMobileEndPicker, setShowQuickMobileEndPicker] =
    useState(false)
  const [quickMobileStartDate, setQuickMobileStartDate] = useState(null)
  const [quickMobileEndDate, setQuickMobileEndDate] = useState(null)

  const fetchDaysOff = useCallback(() => {
    api
      .get(`/api/business_B/${businessId}/dayoff`)
      .then((response) => {
        const updatedDaysOff = response.data.data
          .map((dayOff) => ({
            ...dayOff,
            start: dayjs(dayOff.start).tz('Asia/Taipei'),
            end: dayjs(dayOff.end).tz('Asia/Taipei')
          }))
          .sort((a, b) => (a.start.isBefore(b.start) ? 1 : -1))
        setDaysOff(updatedDaysOff)
      })
      .catch((error) => {
        console.error('Error fetching company days off:', error)
      })
  }, [businessId])

  useEffect(() => {
    fetchDaysOff()
  }, [fetchDaysOff])

  const showModal = () => {
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
    form.resetFields()
  }

  const handleEditCancel = () => {
    setIsEditModalVisible(false)
    setEditRecord(null)
    editForm.resetFields()
  }

  const handleFinish = (values) => {
    const newDayOff = {
      name: values.name,
      start: fromPicker(values.date[0]).format(),
      end: fromPicker(values.date[1]).format()
    }

    api
      .post(`/api/business_B/${businessId}/dayoff`, newDayOff)
      .then(() => {
        message.success('新增休假日成功')
        setIsModalVisible(false)
        fetchDaysOff()
      })
      .catch((error) => {
        message.error('新增休假日失敗，請稍後再試')
        console.error('Error adding company day off:', error)
      })
  }

  const handleEditFinish = (values) => {
    const updatedDayOff = {
      name: values.name,
      start: fromPicker(values.date[0]).format(),
      end: fromPicker(values.date[1]).format()
    }

    console.log('編輯的時間格式:', updatedDayOff)

    api
      .put(
        `/api/business_B/${businessId}/dayoff/${editRecord._id}`,
        updatedDayOff
      )
      .then(() => {
        message.success('更新休假日成功')
        setIsEditModalVisible(false)
        setEditRecord(null)
        fetchDaysOff()
      })
      .catch((error) => {
        message.error('更新休假日失敗，請稍後再試')
        console.error('Error updating company day off:', error)
      })
  }

  const handleYearChange = (value) => {
    setSelectedYear(value)
  }

  const handleEdit = (record) => {
    setEditRecord(record)
    editForm.setFieldsValue({
      name: record.name,
      date: [inTz(record.start), inTz(record.end)]
    })
    setIsEditModalVisible(true)
  }

  const handleDelete = (id) => {
    api
      .delete(`/api/business_B/${businessId}/dayoff/${id}`)
      .then(() => {
        message.success('刪除休假日成功')
        fetchDaysOff()
      })
      .catch((error) => {
        message.error('刪除休假日失敗，請稍後再試')
        console.error('Error deleting company day off:', error)
      })
  }

  const handleCopyRecord = (record) => {
    const recordStart = inTz(record.start)
    setSelectedDate(recordStart)
    const dateKey = recordStart.format('YYYY-MM-DD')

    // 設置選中的時段
    const slots = {}
    let currentTime = recordStart
    const endTime = inTz(record.end)

    while (currentTime.isSameOrBefore(endTime, 'minute')) {
      slots[currentTime.format('HH:mm')] = true
      currentTime = currentTime.add(10, 'minute')
    }

    setSelectedTimeSlots((prev) => ({
      ...prev,
      [dateKey]: slots
    }))

    setIsCopyModalVisible(true)
  }

  const getActionMenu = (record) => ({
    items: [
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: '編輯',
        onClick: () => handleEdit(record)
      },
      {
        key: 'copy',
        icon: <CopyOutlined />,
        label: '複製',
        onClick: () => handleCopyRecord(record)
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: '刪除',
        danger: true,
        onClick: () => handleDelete(record._id)
      }
    ]
  })

  const columns = [
    {
      title: '日期',
      dataIndex: 'start',
      key: 'date',
      render: (text, record) => {
        // 絕對時刻 → 店家時區再比較，否則台灣的整天店休（00:00~23:59）
        // 在 UTC+7 會讀成 23:00~22:59，一天的店休被畫成跨兩天
        const start = inTz(record.start)
        const end = inTz(record.end)

        if (start.isSame(end, 'day')) {
          if (
            start.format('HH:mm') === '00:00' &&
            end.format('HH:mm') === '23:59'
          ) {
            return start.format('YYYY-MM-DD')
          }
          return (
            <div style={{ whiteSpace: 'nowrap' }}>
              {start.format('YYYY-MM-DD')}
              <br />
              {start.format('HH:mm')} - {end.format('HH:mm')}
            </div>
          )
        }
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            {start.format('YYYY-MM-DD')}
            <br />
            {end.format('YYYY-MM-DD')}
          </div>
        )
      }
    },
    {
      title: '店休名稱',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true
    },
    {
      title: '時段',
      key: 'timeSlot',
      render: (_, record) => {
        const start = inTz(record.start)
        const end = inTz(record.end)

        if (
          start.format('HH:mm') === '00:00' &&
          end.format('HH:mm') === '23:59'
        ) {
          return <span style={{ whiteSpace: 'nowrap' }}>整天</span>
        }
        return (
          <span style={{ whiteSpace: 'nowrap' }}>
            {start.format('HH:mm')} - {end.format('HH:mm')}
          </span>
        )
      }
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, record) => (
        <Dropdown menu={getActionMenu(record)} trigger={['click']}>
          <Button type='text' icon={<EllipsisOutlined />} />
        </Dropdown>
      )
    }
  ]

  const getResponsiveColumns = () => {
    if (window.innerWidth <= 576) {
      return [
        columns[0], // 日期
        columns[1], // 店休名稱
        columns[3] // 操作
      ]
    }
    return columns
  }

  const hintText = (
    <>
      按日計算：
      <br />
      以24小時為單位，點擊日期自動帶入整日時間，例如：00:00~23:59，即為1天
      <br />
      按時計算：
      <br />
      如11:00開始休假，14:00恢復上班，請選擇11:00~13:59
    </>
  )

  const generateTimeSlots = () => {
    try {
      const slots = []
      const start = dayjs().startOf('day')
      const startHour = showMidnight ? 0 : 9
      const totalSlots = showMidnight ? 24 * 6 : 15 * 6

      for (let i = 0; i < totalSlots; i++) {
        try {
          const time = start.add(startHour * 60 + i * 10, 'minute')
          if (time.isValid()) {
            slots.push(time.format('HH:mm'))
          }
        } catch (e) {
          console.error('Error generating time slot:', e)
        }
      }

      return slots
    } catch (e) {
      console.error('Error in generateTimeSlots:', e)
      return []
    }
  }

  const handleTimeSlotClick = (slot) => {
    if (!selectedDate) return

    const dateKey = selectedDate.format('YYYY-MM-DD')
    const currentSlots = selectedTimeSlots[dateKey] || {}

    setSelectedTimeSlots((prev) => ({
      ...prev,
      [dateKey]: {
        ...currentSlots,
        [slot]: !currentSlots[slot]
      }
    }))
  }

  // 將休假記錄轉換為時間格子格式
  useEffect(() => {
    if (!selectedDate) return

    const dateStr = selectedDate.format('YYYY-MM-DD')
    const newTimeSlots = {}

    try {
      daysOff.forEach((dayOff) => {
        // 确保创建有效的 dayjs 对象，并进行时区转换
        let startDate, endDate

        try {
          startDate = dayjs(dayOff.start).tz('Asia/Taipei')
          endDate = dayjs(dayOff.end).tz('Asia/Taipei')

          // 验证时间对象是否有效
          if (!startDate.isValid() || !endDate.isValid()) {
            console.warn('Invalid date objects:', {
              start: dayOff.start,
              end: dayOff.end
            })
            return // 跳过这条记录
          }
        } catch (e) {
          console.error('Error parsing dates:', e)
          return // 跳过这条记录
        }

        // 检查是否是选中日期的休假
        if (startDate.format('YYYY-MM-DD') === dateStr) {
          let currentTime = startDate.clone()
          const endOfCurrentDay = startDate.clone().endOf('day')
          const effectiveEndTime = endDate.isSame(startDate, 'day')
            ? endDate
            : endOfCurrentDay

          // 使用普通的比较逻辑替代 isSameOrBefore
          while (currentTime.valueOf() <= effectiveEndTime.valueOf()) {
            try {
              const timeStr = currentTime.format('HH:mm')
              newTimeSlots[timeStr] = true
              currentTime = currentTime.add(10, 'minute')
            } catch (e) {
              console.error('Error in time slot processing:', e)
              break
            }
          }
        }
      })

      setSelectedTimeSlots((prev) => ({
        ...prev,
        [dateStr]: newTimeSlots
      }))
    } catch (error) {
      console.error('Error processing time slots:', error)
      message.error('处理时间格子时出错')
    }
  }, [selectedDate, daysOff])

  // 更新時間格子顯示邏輯
  const renderTimeSlots = () => {
    if (!selectedDate) return null

    const slots = generateTimeSlots()
    const dateKey = selectedDate.format('YYYY-MM-DD')
    const currentSlots = selectedTimeSlots[dateKey] || {}

    return slots.map((slot) => {
      const isSelected = currentSlots[slot]
      return (
        <TimeSlot
          key={slot}
          selected={isSelected}
          onClick={() => handleTimeSlotClick(slot)}
        >
          {slot}
        </TimeSlot>
      )
    })
  }

  // 更新儲存邏輯，避免重複儲存
  const handleSaveTimeSlots = () => {
    if (!selectedDate) return

    const dateKey = selectedDate.format('YYYY-MM-DD')
    const selectedSlots = selectedTimeSlots[dateKey] || {}

    // 將選中的時段轉換為連續的時間段
    const timeRanges = Object.entries(selectedSlots)
      .filter(([_, selected]) => selected)
      .map(([time]) => time)
      .sort()
      .reduce((ranges, time) => {
        if (ranges.length === 0) {
          return [[time, time]]
        }

        const lastRange = ranges[ranges.length - 1]
        const lastTime = lastRange[1]
        const currentTime = dayjs(time, 'HH:mm')
        const prevTime = dayjs(lastTime, 'HH:mm')

        if (currentTime.diff(prevTime, 'minute') === 10) {
          lastRange[1] = time
          return ranges
        }

        return [...ranges, [time, time]]
      }, [])

    // 轉換為 API 所需的格式
    const timeSlots = timeRanges.map(([start, end]) => ({
      start,
      end
    }))

    // 調用新的 API
    api
      .put(`/api/business_B/${businessId}/dayoff/date`, {
        date: dateKey,
        timeSlots
      })
      .then(() => {
        message.success('儲存店休時段成功')
        fetchDaysOff()
        setSelectedTimeSlots((prev) => ({
          ...prev,
          [dateKey]: {}
        }))
      })
      .catch((error) => {
        message.error('儲存店休時段失敗')
        console.error('Error saving time slots:', error)
      })
  }

  const handleOpenCopyModal = () => {
    if (!selectedDate) {
      message.warning('請先選擇日期和時段')
      return
    }

    const dateKey = selectedDate.format('YYYY-MM-DD')
    const currentSlots = selectedTimeSlots[dateKey] || {}

    // 檢查是否有選中的時段
    const hasSelectedSlots = Object.values(currentSlots).some(
      (selected) => selected
    )
    if (!hasSelectedSlots) {
      message.warning('請先選擇要複製的時段')
      return
    }

    // 保存當前選中的時段
    setCopySourceSlots(currentSlots)
    setIsCopyModalVisible(true)
  }

  const handleCopyConfirm = async () => {
    if (!copyDateRange) return

    const startDate = copyDateRange[0]
    const endDate = copyDateRange[1]

    // 使用保存的源時段
    const timeRanges = Object.entries(copySourceSlots)
      .filter(([_, selected]) => selected)
      .map(([time]) => time)
      .sort()
      .reduce((ranges, time) => {
        if (ranges.length === 0) {
          return [[time, time]]
        }

        const lastRange = ranges[ranges.length - 1]
        const lastTime = lastRange[1]
        const currentTime = dayjs(time, 'HH:mm')
        const prevTime = dayjs(lastTime, 'HH:mm')

        if (currentTime.diff(prevTime, 'minute') === 10) {
          lastRange[1] = time
          return ranges
        }

        return [...ranges, [time, time]]
      }, [])

    const timeSlots = timeRanges.map(([start, end]) => ({
      start,
      end
    }))

    // 生成日期範圍內的所有日期
    const dates = []
    let currentDate = startDate.clone()
    while (currentDate.isSameOrBefore(endDate, 'day')) {
      dates.push(currentDate.format('YYYY-MM-DD'))
      currentDate = currentDate.add(1, 'day')
    }

    try {
      await Promise.all(
        dates.map((date) =>
          api.put(`/api/business_B/${businessId}/dayoff/date`, {
            date,
            timeSlots
          })
        )
      )

      message.success('複製時段成功')
      setIsCopyModalVisible(false)
      setCopyDateRange(null)
      setCopySourceSlots({}) // 清空源時段
      fetchDaysOff()
    } catch (error) {
      console.error('複製時段失敗：', error)
      message.error('複製時段失敗')
    }
  }

  const handleCopyCancel = () => {
    setIsCopyModalVisible(false)
    setCopyDateRange(null)
    setCopySourceSlots({}) // 清空源時段
  }

  // 處理快速操作
  const handleQuickAction = (type) => {
    setQuickActionType(type)
    setQuickActionModalVisible(true)
  }

  // 處理快速操作確認
  const handleQuickActionConfirm = () => {
    if (!quickActionDateRange) {
      message.error('請選擇日期')
      return
    }

    const newDayOff = {
      name: quickActionType === 'fullDay' ? '整日店休' : '區間店休',
      start: '',
      end: ''
    }

    if (quickActionType === 'fullDay') {
      // 整日店休：設置為當天 00:00 到 23:59
      newDayOff.start = quickActionDateRange[0].startOf('day').format()
      newDayOff.end = quickActionDateRange[0].endOf('day').format()
    } else {
      // 區間店休：設置為開始日期 00:00 到結束日期 23:59
      newDayOff.start = quickActionDateRange[0].startOf('day').format()
      newDayOff.end = quickActionDateRange[1].endOf('day').format()
    }

    api
      .post(`/api/business_B/${businessId}/dayoff`, newDayOff)
      .then(() => {
        message.success(`新增${newDayOff.name}成功`)
        setQuickActionModalVisible(false)
        setQuickActionDateRange(null)
        fetchDaysOff()
      })
      .catch((error) => {
        message.error(`新增${newDayOff.name}失敗`)
        console.error('Error adding quick action:', error)
      })
  }

  // 确保在组件顶部正确初始化 dayjs
  useEffect(() => {
    // 验证 dayjs 配置
    try {
      const testDate = tzNow()
      if (!testDate.isValid()) {
        console.error('dayjs initialization failed')
      }
      if (!testDate.tz) {
        console.error('timezone plugin not properly initialized')
      }
    } catch (e) {
      console.error('Error checking dayjs configuration:', e)
    }
  }, [])

  const handleMobileStartConfirm = (date) => {
    setMobileStartDate(dayjs(date))
    form.setFieldsValue({
      date: [dayjs(date), form.getFieldValue('date')?.[1]]
    })
    setShowMobileStartPicker(false)
  }

  const handleMobileEndConfirm = (date) => {
    setMobileEndDate(dayjs(date))
    form.setFieldsValue({
      date: [form.getFieldValue('date')?.[0], dayjs(date)]
    })
    setShowMobileEndPicker(false)
  }

  // 添加一個判斷螢幕寬度的函數
  const isMobileView = () => {
    return window.innerWidth <= 820
  }

  const handleQuickMobileStartConfirm = (date) => {
    const newDate = dayjs(date)
    setQuickMobileStartDate(newDate)
    setQuickActionDateRange((prev) => [newDate, prev?.[1] || newDate])
    setShowQuickMobileStartPicker(false)
  }

  const handleQuickMobileEndConfirm = (date) => {
    const newDate = dayjs(date)
    setQuickMobileEndDate(newDate)
    setQuickActionDateRange((prev) => [prev?.[0], newDate])
    setShowQuickMobileEndPicker(false)
  }

  return (
    <Container>
      <QuickActionCard>
        <div className='action-buttons'>
          <QuickActionButton
            className='full-day'
            onClick={() => handleQuickAction('fullDay')}
          >
            <CalendarOutlined />
            整日店休
          </QuickActionButton>

          <QuickActionButton
            className='range'
            onClick={() => handleQuickAction('range')}
          >
            <FieldTimeOutlined />
            區間店休
          </QuickActionButton>

          <QuickActionButton className='add-new' onClick={showModal}>
            <PlusCircleOutlined />
            新增店休
          </QuickActionButton>
        </div>
      </QuickActionCard>

      <Card title='選擇與查看店休時段'>
        <DatePicker
          value={selectedDate}
          onChange={setSelectedDate}
          format='YYYY-MM-DD'
          placeholder='選擇日期'
          style={{ width: '100%', marginBottom: '16px' }}
        />

        {selectedDate && (
          <>
            <Hint>
              <div className='flex justify-between items-center mb-2'>
                <div>使用說明：</div>
                <Button
                  type='text'
                  onClick={() => setIsCollapsed(!isCollapsed)}
                  icon={isCollapsed ? <DownOutlined /> : <UpOutlined />}
                >
                  {isCollapsed ? '展開' : '收起'}
                </Button>
              </div>

              {!isCollapsed && (
                <>
                  • 點選時段格子可切換該時段的營業/休息狀態
                  <br />
                  •
                  系統會將所選時段納入休息時間。例如：選擇14:00作為休息結束時間，代表14:00仍在休息，14:01才開始營業
                  <br />•
                  預約時段若剛好在休假開始前結束則允許（例：17:00的預約到18:00結束可以），但若延伸到休假時間內則會被過濾（例：17:30的預約會被過濾）
                </>
              )}
            </Hint>

            <div style={{ marginBottom: '16px' }}>
              <Switch
                checked={showMidnight}
                onChange={setShowMidnight}
                checkedChildren='顯示凌晨時段'
                unCheckedChildren='隱藏凌晨時段'
              />
            </div>

            <Legend>
              <div className='legend-item'>
                <div className='color-box' style={{ background: '#bf1c5f' }} />
                <span>店休時段</span>
              </div>
              <div className='legend-item'>
                <div className='color-box' style={{ background: '#f5f5f5' }} />
                <span>營業時段</span>
              </div>
            </Legend>

            <TimeGrid>{renderTimeSlots()}</TimeGrid>

            <Space
              style={{ marginTop: '16px', width: '100%' }}
              direction='vertical'
            >
              <Button
                type='primary'
                onClick={handleSaveTimeSlots}
                style={{ width: '100%' }}
              >
                儲存店休時段
              </Button>
              <Button onClick={handleOpenCopyModal} style={{ width: '100%' }}>
                複製時段到其他日期
              </Button>
            </Space>
          </>
        )}
      </Card>

      {/* 店休記錄列表 */}
      <Card title='店休記錄' style={{ marginTop: '24px' }}>
        <Table
          dataSource={daysOff}
          columns={getResponsiveColumns()}
          rowKey='_id'
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: daysOff.length,
            onChange: (page) => setCurrentPage(page),
            showTotal: (total) => `共 ${total} 筆`,
            showSizeChanger: false,
            showQuickJumper: true,
            position: ['bottomCenter'],
            size: 'small',
            locale: {
              items_per_page: '筆/頁',
              jump_to: '跳至',
              jump_to_confirm: '確定',
              page: '頁',
              prev_page: '上一頁',
              next_page: '下一頁',
              prev_5: '向前 5 頁',
              next_5: '向後 5 頁'
            }
          }}
          size='small'
          scroll={{ x: true }}
        />
      </Card>

      <Modal
        title='新增店休具體時段'
        open={isModalVisible}
        footer={null}
        style={{ top: 20 }}
        onCancel={handleCancel}
      >
        <Form form={form} layout='vertical' onFinish={handleFinish}>
          <Form.Item
            name='name'
            label='休假日名稱'
            rules={[{ required: true, message: '請輸入休假日名稱' }]}
          >
            <Input placeholder='請輸入休假日名稱' />
          </Form.Item>
          <Form.Item
            name='date'
            label={
              <span>
                日期
                <Tooltip title={hintText}>
                  <InfoCircleOutlined style={{ marginLeft: '8px' }} />
                </Tooltip>
              </span>
            }
            rules={[{ required: true, message: '請選擇日期和時間' }]}
          >
            {isMobileView() ? (
              <div>
                <Space direction='vertical' style={{ width: '100%' }}>
                  <Button block onClick={() => setShowMobileStartPicker(true)}>
                    {mobileStartDate
                      ? mobileStartDate.format('YYYY-MM-DD HH:mm')
                      : '選擇開始時間'}
                  </Button>
                  <Button block onClick={() => setShowMobileEndPicker(true)}>
                    {mobileEndDate
                      ? mobileEndDate.format('YYYY-MM-DD HH:mm')
                      : '選擇結束時間'}
                  </Button>
                </Space>

                <MobileDatePicker
                  visible={showMobileStartPicker}
                  onClose={() => setShowMobileStartPicker(false)}
                  precision='minute'
                  onConfirm={handleMobileStartConfirm}
                  min={tzNow().toDate()}
                  max={new Date(2030, 11, 31)}
                />

                <MobileDatePicker
                  visible={showMobileEndPicker}
                  onClose={() => setShowMobileEndPicker(false)}
                  precision='minute'
                  onConfirm={handleMobileEndConfirm}
                  min={mobileStartDate ? mobileStartDate.toDate() : tzNow().toDate()}
                  max={new Date(2030, 11, 31)}
                />
              </div>
            ) : (
              <RangePicker
                style={{ width: '100%' }}
                format='YYYY-MM-DD HH:mm'
                showTime={{
                  format: 'HH:mm',
                  defaultValue: [
                    dayjs('00:00:00', 'HH:mm:ss'),
                    dayjs('23:59:59', 'HH:mm:ss')
                  ]
                }}
                separator='至'
                placeholder={['開始時間', '結束時間']}
              />
            )}
          </Form.Item>
          <Form.Item>
            <Button type='primary' htmlType='submit'>
              新增店休
            </Button>
            <Button onClick={handleCancel} style={{ marginLeft: 8 }}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title='編輯店休假日'
        open={isEditModalVisible}
        style={{ top: 20 }}
        footer={null}
        onCancel={handleEditCancel}
      >
        <Form form={editForm} layout='vertical' onFinish={handleEditFinish}>
          <Form.Item
            name='name'
            label='休假日名稱'
            rules={[{ required: true, message: '請輸入休假日名稱' }]}
          >
            <Input placeholder='輸入休假日名稱' />
          </Form.Item>
          <Form.Item
            name='date'
            label='日期'
            rules={[{ required: true, message: '請選擇日期和時間' }]}
          >
            <RangePicker
              style={{ width: '100%' }}
              format='YYYY-MM-DD HH:mm'
              showTime={{
                format: 'HH:mm',
                defaultValue: [
                  dayjs('00:00:00', 'HH:mm:ss'),
                  dayjs('23:59:59', 'HH:mm:ss')
                ]
              }}
              separator='至'
              placeholder={['開始時間', '結束時間']}
            />
          </Form.Item>
          <Form.Item>
            <Button type='primary' htmlType='submit'>
              保存
            </Button>
            <Button onClick={handleEditCancel} style={{ marginLeft: 8 }}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title='複製時段'
        open={isCopyModalVisible}
        onOk={handleCopyConfirm}
        onCancel={handleCopyCancel}
        okText='確認'
        cancelText='取消'
      >
        <div className='date-range'>
          <h4>選擇要複製到的日期範圍：</h4>
          <RangePicker
            style={{ width: '100%' }}
            value={copyDateRange}
            onChange={setCopyDateRange}
          />
        </div>
      </Modal>

      {/* 快速操作 Modal */}
      <Modal
        title={
          quickActionType === 'fullDay'
            ? '快速新增整日休假'
            : '快速新增區間休假'
        }
        open={quickActionModalVisible}
        onOk={handleQuickActionConfirm}
        onCancel={() => {
          setQuickActionModalVisible(false)
          setQuickActionDateRange(null)
          setQuickMobileStartDate(null)
          setQuickMobileEndDate(null)
        }}
        okText='確認'
        cancelText='取消'
      >
        {isMobileView() ? (
          <div>
            {quickActionType === 'fullDay' ? (
              // 整日休假 - 單日選擇
              <>
                <Button
                  block
                  onClick={() => setShowQuickMobileStartPicker(true)}
                  style={{ marginBottom: '16px' }}
                >
                  {quickMobileStartDate
                    ? quickMobileStartDate.format('YYYY-MM-DD')
                    : '選擇休假日期'}
                </Button>
                <MobileDatePicker
                  visible={showQuickMobileStartPicker}
                  onClose={() => setShowQuickMobileStartPicker(false)}
                  precision='day'
                  onConfirm={(date) => {
                    const newDate = dayjs(date)
                    setQuickMobileStartDate(newDate)
                    setQuickActionDateRange([newDate, newDate])
                    setShowQuickMobileStartPicker(false)
                  }}
                  min={tzNow().toDate()}
                  max={new Date(2030, 11, 31)}
                />
              </>
            ) : (
              // 區間休假 - 日期範圍選擇
              <>
                <Space direction='vertical' style={{ width: '100%' }}>
                  <Button
                    block
                    onClick={() => setShowQuickMobileStartPicker(true)}
                  >
                    {quickMobileStartDate
                      ? quickMobileStartDate.format('YYYY-MM-DD')
                      : '選擇開始日期'}
                  </Button>
                  <Button
                    block
                    onClick={() => setShowQuickMobileEndPicker(true)}
                  >
                    {quickMobileEndDate
                      ? quickMobileEndDate.format('YYYY-MM-DD')
                      : '選擇結束日期'}
                  </Button>
                </Space>

                <MobileDatePicker
                  visible={showQuickMobileStartPicker}
                  onClose={() => setShowQuickMobileStartPicker(false)}
                  precision='day'
                  onConfirm={handleQuickMobileStartConfirm}
                  min={tzNow().toDate()}
                  max={new Date(2030, 11, 31)}
                />

                <MobileDatePicker
                  visible={showQuickMobileEndPicker}
                  onClose={() => setShowQuickMobileEndPicker(false)}
                  precision='day'
                  onConfirm={handleQuickMobileEndConfirm}
                  min={
                    quickMobileStartDate
                      ? quickMobileStartDate.toDate()
                      : tzNow().toDate()
                  }
                  max={new Date(2030, 11, 31)}
                />
              </>
            )}
          </div>
        ) : // 桌面版保持原有的 DatePicker/RangePicker
        quickActionType === 'fullDay' ? (
          <DatePicker
            style={{ width: '100%' }}
            value={quickActionDateRange?.[0]}
            onChange={(date) =>
              setQuickActionDateRange(date ? [date, date] : null)
            }
          />
        ) : (
          <RangePicker
            style={{ width: '100%' }}
            value={quickActionDateRange}
            onChange={setQuickActionDateRange}
          />
        )}
      </Modal>
    </Container>
  )
}

export default CompanyDaysOff
